/**
 * App Shell
 *
 * Main layout with a right-hand navigation panel, a top header (logo left,
 * user email + sign out right), and a persistent project/run context bar.
 * Styled to match the edna application using FTI design-system tokens.
 */

import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Icon, Button, Flex, Logo, useTheme, spacing, fontSize } from '@fti/design-system';
import { useUser, useProviderLogout } from '../../../auth/hooks';
import { DevModeSettings } from '../../../components/DevModeSettings';
import { GlobalContextBar } from './GlobalContextBar';
import { useMediaSimUser } from './MediaSimUserContext';

interface NavItem {
  path: string;
  label: string;
  icon?: string;
  /** Match by prefix so nested routes keep the item active. */
  match?: (pathname: string) => boolean;
  adminOnly?: boolean;
  /** Sub-items shown indented when this section is active. */
  children?: NavItem[];
}

const NAV_ITEMS: NavItem[] = [
  { path: '/home', label: 'Welcome', icon: 'home' },
  {
    path: '/simulations/projects',
    label: 'Projects',
    // "Projects" is the active section for the list/wizard AND all four
    // project sub-pages, so its sub-items stay visible while working inside a
    // project. (Excludes /simulations/dashboard, which is its own section.)
    match: (p) =>
      p.startsWith('/simulations/projects') ||
      ['/simulations/overview', '/simulations/messages', '/simulations/runs', '/simulations/results'].includes(p),
    children: [
      { path: '/simulations/overview', label: 'Overview', match: (p) => p === '/simulations/overview' },
      { path: '/simulations/messages', label: 'Messages', match: (p) => p === '/simulations/messages' },
      { path: '/simulations/runs', label: 'Runs', match: (p) => p === '/simulations/runs' },
      { path: '/simulations/results', label: 'Results', match: (p) => p === '/simulations/results' },
    ],
  },
  {
    path: '/simulations/dashboard',
    label: 'Dashboard',
    match: (p) => p.startsWith('/simulations/dashboard'),
  },
  {
    path: '/publications',
    label: 'Explore Publications',
    match: (p) => p === '/publications',
  },
  {
    path: '/publications/request',
    label: 'Request Publications',
    match: (p) => p === '/publications/request',
  },
  { path: '/users', label: 'User Management', icon: 'settings', adminOnly: true },
];

export function AppShell() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useUser();
  const { signoutRedirect } = useProviderLogout();
  const { isAdmin } = useMediaSimUser();

  const navItems = NAV_ITEMS.filter((item) => !item.adminOnly || isAdmin);

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const borderColor = theme.colors.border.default;
  const bgPaper = theme.colors.background.paper;
  const bgDefault = theme.colors.background.default;
  const primaryColor = theme.colors.primary[600];

  const isActive = (item: NavItem) =>
    item.match ? item.match(location.pathname) : location.pathname === item.path;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: bgDefault }}>
      {/* Header */}
      <header style={{
        height: '64px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: `0 ${spacing.lg}`,
        background: bgPaper,
        borderBottom: `1px solid ${borderColor}`,
      }}>
        <Flex align="center" gap="md">
          <Logo size="medium" variant="default" onClick={() => navigate('/home')} alt="FTI Consulting" />
          <span
            style={{ cursor: 'pointer', fontSize: '1.1rem', fontWeight: 700, color: textColor, letterSpacing: '0.02em' }}
            onClick={() => navigate('/home')}
          >
            Media Simulation
          </span>
        </Flex>
        <Flex align="center" gap="sm">
          <DevModeSettings compact />
          {user?.email && (
            <span style={{ fontSize: fontSize.sm, color: mutedColor }}>{user.email}</span>
          )}
          <Button variant="outline" color="error" size="sm" onClick={() => signoutRedirect()}>
            Sign out
          </Button>
        </Flex>
      </header>

      <div style={{ display: 'flex', flex: 1, minHeight: 0, height: 'calc(100vh - 64px)' }}>
        {/* Left-hand Sidebar */}
        <nav style={{
          width: '220px',
          minWidth: '220px',
          background: bgPaper,
          borderRight: `1px solid ${borderColor}`,
          overflowY: 'auto',
          padding: `${spacing.md} 0`,
        }}>
          {navItems.map((item) => {
            const active = isActive(item);
            const rowStyle = (isActive: boolean, indent: boolean): React.CSSProperties => ({
              display: 'flex',
              alignItems: 'center',
              gap: spacing.sm,
              padding: `${spacing.xs} ${spacing.md}`,
              paddingLeft: indent ? spacing.xl : spacing.md,
              margin: `1px ${spacing.sm}`,
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: fontSize.sm,
              fontWeight: isActive ? 600 : 400,
              color: isActive ? primaryColor : textColor,
              background: isActive ? `${primaryColor}10` : 'transparent',
              transition: 'background 0.15s',
            });
            // Show a section's sub-items whenever that section (or one of its
            // children) is the active route.
            const showChildren = !!item.children && active;
            return (
              <div key={item.path}>
                <div onClick={() => navigate(item.path)} style={rowStyle(active, false)}>
                  {item.icon && <Icon name={item.icon} size="sm" />}
                  <span style={{ whiteSpace: 'normal' }}>{item.label}</span>
                </div>
                {showChildren && item.children!.map((child) => (
                  <div
                    key={child.path}
                    onClick={() => navigate(child.path)}
                    style={rowStyle(isActive(child), true)}
                  >
                    <span style={{ whiteSpace: 'normal' }}>{child.label}</span>
                  </div>
                ))}
              </div>
            );
          })}
        </nav>

        {/* Main Content */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <GlobalContextBar />
          <main style={{ flex: 1, padding: spacing.lg, overflow: 'auto' }}>
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
